import { useMemo } from 'react'
import type { AnalysisSnapshot, AnalysisTimeRangeKey } from '../../../../types/analysis'
import type { TimelineElement } from '../../../../types/timelineElement'
import type { LiveModuleHeadline, LiveModuleRecord, LiveModuleSparkline } from '../LiveModuleCard'
import { bucketize, calculateDelta, timeRangeToDateRange } from './bucketize'

export type UseUploadProofBacklogModuleParams = {
  workspaceId: string
  snapshot: AnalysisSnapshot
  elements?: TimelineElement[]
  timeRange: AnalysisTimeRangeKey
  refreshKey?: number
  now?: Date
}

export type UseUploadProofBacklogModuleResult = {
  headline: LiveModuleHeadline
  sparkline: LiveModuleSparkline
  records: LiveModuleRecord[]
  loading: boolean
  error: Error | null
}

const parseDate = (value: unknown): Date | null => {
  if (!value) return null
  if (value instanceof Date) return value
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') return (value as { toDate: () => Date }).toDate()
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date
  }
  return null
}

const hasProof = (item: object): boolean =>
  ('proofFileUrl' in item && Boolean(item.proofFileUrl)) ||
  ('proofSubmissionId' in item && Boolean(item.proofSubmissionId))

export const useUploadProofBacklogModule = ({
  workspaceId,
  snapshot,
  elements = [],
  timeRange,
  refreshKey,
  now = new Date(),
}: UseUploadProofBacklogModuleParams): UseUploadProofBacklogModuleResult => useMemo(() => {
  void snapshot
  void refreshKey

  const backlog = elements
    .filter((element) => element.workspaceId === workspaceId)
    .flatMap((element) => (element.checklist ?? []).map((item) => ({ item, assignedAt: parseDate(item.assignedAt) })))
    .filter(({ item }) => item.requiresProofFile === true && !hasProof(item))

  if (!workspaceId || backlog.length === 0) {
    return {
      headline: { value: 0, label: 'items pending proof' },
      sparkline: { points: [], direction: 'up-is-bad' },
      records: [],
      loading: false,
      error: null,
    }
  }

  const { from, to } = timeRangeToDateRange(timeRange, now)
  const datedBacklog = backlog.filter(({ assignedAt }) => assignedAt && assignedAt >= from && assignedAt <= to)
  const points = datedBacklog.length > 0
    ? bucketize(datedBacklog.map(({ assignedAt }) => assignedAt), timeRange, now)
    : []

  const records: LiveModuleRecord[] = backlog
    .map(({ item, assignedAt }) => ({
      item,
      ageInDays: assignedAt ? Math.max(0, Math.floor((now.getTime() - assignedAt.getTime()) / 86_400_000)) : 0,
    }))
    .sort((left, right) => right.ageInDays - left.ageInDays)
    .slice(0, 5)
    .map(({ item, ageInDays }) => ({
      id: item.id,
      primary: item.text || item.description || 'Untitled checklist item',
      secondary: `${ageInDays} day${ageInDays === 1 ? '' : 's'} in backlog`,
      numeric: ageInDays,
      drillTarget: 'UPLOAD',
    }))

  // More proof-gated work entering the backlog is unhealthy, so up is bad.
  return {
    headline: { value: backlog.length, label: 'items pending proof', delta: calculateDelta(points) },
    sparkline: { points, direction: 'up-is-bad' },
    records,
    loading: false,
    error: null,
  }
}, [workspaceId, snapshot, elements, timeRange, refreshKey, now])
